import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowRight, ShieldCheck, QrCode, Globe } from 'lucide-react';

export const Hero = () => {
  return (
    <section
      id="hero"
      className="hero-gradient pt-24 sm:pt-28 pb-16 sm:pb-20"
      data-testid="hero-section"
    >
      <div className="container-main">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Copy */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="font-mono-accent text-xs font-medium uppercase tracking-[0.18em] text-[#7C3AED] mb-4">
              Next-gen digital wallet
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0B0B10] mb-6 leading-tight">
              One wallet for{' '}
              <span style={{ color: '#7C3AED' }}>global</span>{' '}
              payments.
            </h1>
            <p className="text-base md:text-lg text-[#64647A] max-w-lg mb-8 leading-relaxed">
              QR payments, SWIFT/SEPA transfers, and cross-border payouts — built for speed, compliance, and your peace of mind. No foreign cards required.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <Button
                data-testid="hero-primary-cta"
                className="bg-[#7C3AED] text-white hover:bg-[#6D28D9] rounded-xl h-12 px-6 text-sm shadow-sm hover:shadow-md btn-press"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Get early access
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                data-testid="hero-secondary-cta"
                variant="outline"
                className="bg-white text-[#12121A] border border-[#E7E7EF] hover:bg-[#F7F7FB] rounded-xl h-12 px-6 text-sm"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Learn more
              </Button>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="bg-[#F5F3FF] text-[#7C3AED] border-0 rounded-lg px-3 py-1.5 text-xs font-medium">
                <ShieldCheck className="h-3 w-3 mr-1.5" />
                SWIFT/SEPA ready
              </Badge>
              <Badge variant="secondary" className="bg-[#F5F3FF] text-[#7C3AED] border-0 rounded-lg px-3 py-1.5 text-xs font-medium">
                <QrCode className="h-3 w-3 mr-1.5" />
                QR payments
              </Badge>
              <Badge variant="secondary" className="bg-[#F5F3FF] text-[#7C3AED] border-0 rounded-lg px-3 py-1.5 text-xs font-medium">
                <Globe className="h-3 w-3 mr-1.5" />
                180+ countries
              </Badge>
            </div>
          </motion.div>

          {/* Right: Product mock card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center lg:justify-end"
          >
            <div className="relative">
              {/* Main card */}
              <div className="bg-white border border-[#E7E7EF] rounded-2xl shadow-[0_1px_0_rgba(10,10,20,0.06),0_12px_30px_rgba(10,10,20,0.08)] p-6 sm:p-8 w-full max-w-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-xl bg-[#F5F3FF] flex items-center justify-center">
                    <svg width="20" height="20" viewBox="0 0 40 40" fill="none">
                      <ellipse cx="20" cy="20" rx="18" ry="18" stroke="#7C3AED" strokeWidth="2" />
                      <path d="M6 20h28" stroke="#7C3AED" strokeWidth="1.5" />
                      <circle cx="20" cy="14" r="2" fill="#7C3AED" />
                      <circle cx="20" cy="26" r="2" fill="#7C3AED" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0B0B10]">TexSolution Wallet</p>
                    <p className="text-xs text-[#64647A]">Multi-currency balance</p>
                  </div>
                </div>

                {/* Balance display */}
                <div className="bg-[#F5F3FF] rounded-xl p-4 mb-4">
                  <p className="text-xs text-[#64647A] mb-1 font-mono-accent">AVAILABLE BALANCE</p>
                  <p className="text-3xl font-bold text-[#0B0B10]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    $12,450<span className="text-lg text-[#64647A]">.00</span>
                  </p>
                </div>

                {/* Recent transactions */}
                <p className="text-xs font-medium text-[#64647A] mb-3 font-mono-accent uppercase tracking-wider">Recent transfers</p>
                <div className="space-y-3">
                  {[
                    { to: 'Payment to vendor', amount: '-$1,200.00', flag: '🇬🇧', status: 'Completed' },
                    { to: 'QR Purchase', amount: '-$89.50', flag: '🇯🇵', status: 'Completed' },
                    { to: 'Incoming transfer', amount: '+$5,000.00', flag: '🇸🇬', status: 'Received' },
                  ].map((tx, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-[#F5F3FF] last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{tx.flag}</span>
                        <div>
                          <p className="text-sm font-medium text-[#0B0B10]">{tx.to}</p>
                          <p className="text-xs text-[#64647A]">{tx.status}</p>
                        </div>
                      </div>
                      <p className={`text-sm font-medium ${tx.amount.startsWith('+') ? 'text-[#16A34A]' : 'text-[#0B0B10]'}`}>
                        {tx.amount}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -top-3 -right-3 bg-[#7C3AED] text-white rounded-xl px-3 py-1.5 text-xs font-medium shadow-md">
                Instant transfers
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
